/**
 * 事件考验答题弹窗
 *
 * 教育红线（GDD 2.4 / CLAUDE.md 铁律 6）：
 * - **答错不惩罚**：文案不出现"失败/错误/扣分"，只说明"这次没答对，我们记下来"；
 * - 答对给额外智慧值，答错也有保底 —— 收益差异体现在数值上，不体现在情绪上；
 * - 选项点击后立即展示讲解，先讲「为什么」再继续；
 * - 答题期间不可按 Esc/点背景关闭，避免误触跳过（但也不给"跳过"压力）。
 */

import type { KnowledgeCard } from "@chuandeng/shared";
import { triggerLabel, type QuizPrompt } from "../game/quiz.js";
import { esc } from "./dom.js";
import { type ModalHandle, openModal } from "./modal.js";

export interface QuizOptions {
  /** 本局已答几道（用于顶部进度提示，让孩子知道还剩几道） */
  progressLabel: string;
  onAnswered(correct: boolean): void;
  /** 弹窗关闭后回调（用于接着弹下一题） */
  onClose?(): void;
}

export function askQuiz(prompt: QuizPrompt, options: QuizOptions): ModalHandle {
  const card: KnowledgeCard = prompt.card;
  const letters = ["A", "B", "C", "D"];

  const handle = openModal({
    eyebrow: `${triggerLabel(prompt.trigger)} · ${options.progressLabel}`,
    title: "来一道题吧",
    dismissible: false,
    bodyHtml: `
      <p class="quiz-question">${esc(card.quiz.question)}</p>
      <div class="codex-meta">
        <span class="badge badge--info">${esc(card.title)}</span>
        ${
          prompt.includesDraft
            ? `<span class="badge badge--draft">此题待人工审核</span>`
            : `<span class="badge badge--ok">已审核</span>`
        }
      </div>
      <ul class="quiz-options" data-quiz>
        ${card.quiz.options
          .map(
            (option, index) => `<li>
              <button class="quiz-option" type="button" data-index="${index}">
                <span class="key">${letters[index] ?? "?"}</span>
                <span>${esc(option)}</span>
              </button>
            </li>`,
          )
          .join("")}
      </ul>
      <div data-feedback hidden></div>`,
    actions: [{ label: "继续", value: "continue", disabled: true }],
    onAction: () => handle.close(),
    ...(options.onClose ? { onClose: options.onClose } : {}),
  });

  const list = handle.element.querySelector<HTMLElement>("[data-quiz]");
  const feedback = handle.element.querySelector<HTMLElement>("[data-feedback]");
  let answered: boolean | null = null;

  list?.addEventListener("click", (event) => {
    if (answered !== null) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest<HTMLElement>("[data-index]");
    if (!button) return;
    const index = Number.parseInt(button.dataset["index"] ?? "", 10);
    if (!Number.isFinite(index)) return;

    const correct = index === card.quiz.answerIndex;
    answered = correct;

    for (const node of list.querySelectorAll<HTMLButtonElement>(".quiz-option")) {
      node.disabled = true;
      const nodeIndex = Number.parseInt(node.dataset["index"] ?? "", 10);
      if (nodeIndex === card.quiz.answerIndex) node.classList.add("is-correct");
      else if (nodeIndex === index) node.classList.add("is-wrong");
    }

    if (feedback) {
      feedback.hidden = false;
      feedback.innerHTML = `
        <div class="quiz-explain">
          <strong>${correct ? "✓ 答对了！智慧之光更盛。" : "✗ 这次没答对，没关系 —— 火种还是往前走了一步。"}</strong><br />
          ${esc(card.quiz.explanation)}
          ${
            correct
              ? ""
              : `<br /><span style="color:var(--ink-soft)">这道题已经放进你的错题集，过几天我们会再来看看它。</span>`
          }
        </div>`;
    }

    handle.setActions([{ label: "继续", value: "continue" }]);
    options.onAnswered(correct);
  });

  return handle;
}
